import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useQuery } from "@apollo/client";
import dayjs from "dayjs";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import DataTable from "components/DataTable";
import DatePicker from "components/DatePicker";
import Header from "components/Header";
import Select from "components/Select";
import StatusModal from "components/StatusModal";
import { LoadingSpinnerSVG } from "components/SVGIcons";
import {
  FILTER_BY_LIST,
  REPORTS_HEADER_LIST,
  REPORT_TYPE_LIST,
  ReportAction,
  isReportInProgress
} from "constants/haulpay-broker";
import { usePullToRefresh } from "context/PullToRefreshContext";
import { resolveReportDateRange } from "helpers/reportDateRange";
import { showErrorMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import MainLayout from "layouts/Main";
import { IReportForm } from "models/HaulPay";
import {
  getCarrierReport,
  getHistoryTransactionAPI,
  getReportJob,
  getReportURL
} from "services/api/haulpay";
import { selectAccount } from "store/auth/selector";
import { selectFactoringProfile, selectUserDetails } from "store/user/selector";
import { QUERY_MY_REPORTS, QUERY_MY_REPORT_DETAILS } from "./query";
import styles from "./style.module.scss";
import { useUserFeature } from "hooks/useUserFeature";

interface IReportList {
  id: number;
  created: string;
  reportType: string;
  details: string;
  status?: string | null;
  error?: string | null;
}

const REPORT_POLL_INTERVAL_MS = 5000;
const FIRST_PAGE_VARIABLES = { sortBy: "-created", orderBy: null, first: null, after: null };

export default function BrokerReports() {
  const { isDesktop } = useDeviceType();
  const userDetails = useSelector(selectUserDetails);
  const factoringProfile = useSelector(selectFactoringProfile);
  const { userPermission } = useSelector(selectAccount);
  const { isCarrier } = useUserFeature();
  const isOffPlatformUser =
    userPermission?.available_contract_types?.SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM ?? false;
  const availableReportTypes = useMemo(() => {
    if (isCarrier) {
      return REPORT_TYPE_LIST;
    }
    const reportVisibility = factoringProfile?.report_visibility ?? {};
    return REPORT_TYPE_LIST.filter(
      (r) =>
        (r.value !== "off_platform_self_finance_report" || isOffPlatformUser) &&
        reportVisibility[r.value] !== false
    );
  }, [isCarrier, isOffPlatformUser, factoringProfile]);
  const profileLoaded = isCarrier || Boolean(factoringProfile);
  const noReportsAvailable = profileLoaded && availableReportTypes.length === 0;
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportList, setReportList] = useState<IReportList[]>([]);
  const [selectedId, setSelectedId] = useState<number>();
  const [pendingReportId, setPendingReportId] = useState<string>();
  const [isConfirmModalOpen, toggleConfirmModal, setConfirmModalOpen] = useToggle();
  const [isReportModalOpen, toggleReportModal] = useToggle();

  const tableRef = useRef<HTMLDivElement | null>(null);
  const [isScroll, setIsScroll] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [endCursor, setEndCursor] = useState();

  const { data, refetch } = useQuery(QUERY_MY_REPORTS, { variables: { sortBy: "-created" } });
  const { refetch: myReportRefetch, loading } = useQuery(QUERY_MY_REPORT_DETAILS, {
    variables: { id: selectedId },
    skip: !selectedId,
    fetchPolicy: "cache-and-network"
  });

  usePullToRefresh(() => {
    setIsScroll(false);
    return refetch({ after: null });
  });

  const {
    control,
    formState: { isSubmitting },
    handleSubmit
  } = useForm<Partial<IReportForm>>({
    values: {
      reportType: availableReportTypes[0]
    }
  });

  const onSubmit = useCallback(
    async (data: IReportForm) => {
      if (pendingReportId) {
        return;
      }
      try {
        const { start, end } = resolveReportDateRange(
          data.filterBy,
          data.createdGte,
          data.createdLte
        );
        if (dayjs(end).isBefore(dayjs(start))) {
          showErrorMessage("End date must be on or after the start date.");
          return;
        }
        const formData = {
          factoring_id: userDetails?.factoring_id,
          report_type: data.reportType.value,
          created_gte: start,
          created_lte: end
        };

        const getReport = isCarrier
          ? getCarrierReport({
              datetime_start: start,
              datetime_end: end
            })
          : data.reportType.value !== "wallet_transaction"
            ? getReportURL({ ...formData, async: true })
            : getHistoryTransactionAPI({
                download: true,
                created_gte: formData.created_gte,
                created_lte: formData.created_lte
              });

        toggleConfirmModal();
        getReport
          .then((response: any) => {
            const reportFileId =
              response?.status === 202 ? response?.data?.report_file_id : undefined;
            if (reportFileId) {
              setPendingReportId(String(reportFileId));
              setIsScroll(false);
              void refetch(FIRST_PAGE_VARIABLES);
              return;
            }
            refetch().then(() => {
              toggleReportModal();
            });
          })
          .catch((error) => {
            setConfirmModalOpen(false);
            const errorMessage = error?.response?.data?.funding_requests;
            if (errorMessage) {
              showErrorMessage(errorMessage);
            } else {
              showErrorMessage(
                "There was an error requesting this report. Please try again later."
              );
            }
          });
      } catch (error: any) {
        showErrorMessage(error.message);
      }
    },
    [isCarrier, pendingReportId]
  );

  const downloadReport = async (id: number) => {
    setSelectedId(id);
    try {
      const response = await myReportRefetch({
        id: id
      });
      const url = response?.data?.report?.downloadUrl;
      if (url) {
        window.open(url, "_blank");
      } else {
        showErrorMessage("This report is no longer available. Please request it again.");
      }
      setSelectedId(undefined);
    } catch (error: any) {
      showErrorMessage(error.message);
    }
  };

  const onScroll = async () => {
    if (tableRef.current) {
      const { scrollTop, offsetHeight, scrollHeight } = tableRef.current;
      if (scrollTop + offsetHeight + 1 >= scrollHeight && hasNextPage) {
        setIsScroll(true);
        await refetch({
          orderBy: "-time_posted",
          first: 20,
          after: endCursor
        });
      }
    }
  };

  useEffect(() => {
    if (data) {
      if (isScroll) {
        setReportList((prev) => [...prev, ...data.myReports.data]);
      } else {
        setReportList(data.myReports.data);
      }
      setHasNextPage(data.myReports.pageInfo.hasNextPage);
      setEndCursor(data.myReports.pageInfo.endCursor);
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, []);

  const hasReportInProgress = useMemo(
    () => reportList.some((report) => isReportInProgress(report.status)),
    [reportList]
  );

  useEffect(() => {
    if (!hasReportInProgress) {
      return undefined;
    }
    const timer = setInterval(() => {
      setIsScroll(false);
      void refetch(FIRST_PAGE_VARIABLES);
    }, REPORT_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasReportInProgress]);

  useEffect(() => {
    if (!pendingReportId) {
      return undefined;
    }
    let finished = false;
    const timer = setInterval(() => {
      getReportJob(pendingReportId)
        .then(({ data: job }) => {
          if (finished || isReportInProgress(job.status)) {
            return;
          }
          finished = true;
          setPendingReportId(undefined);
          setIsScroll(false);
          void refetch(FIRST_PAGE_VARIABLES);
          if (job.status === "complete") {
            toggleReportModal();
          } else {
            showErrorMessage(
              job.error || "There was an error requesting this report. Please try again later."
            );
          }
        })
        .catch(() => undefined);
    }, REPORT_POLL_INTERVAL_MS);
    return () => {
      finished = true;
      clearInterval(timer);
    };
  }, [pendingReportId]);

  const enhancedReportList = useMemo(() => {
    return reportList.map((data) => ({
      ...data,
      action: {
        downloadReport: () => {
          downloadReport(data.id);
        },
        isFetching: loading,
        selectedId: selectedId,
        id: data.id,
        status: data.status,
        error: data.error
      }
    }));
  }, [reportList, loading, downloadReport]);

  const reportHeaderList = useMemo(() => REPORTS_HEADER_LIST, [loading]);

  return (
    <MainLayout desktopTitle="Reports" customHeader={<Header label="Reports" isHideDesktop />}>
      <div className={styles.reportsContainer} ref={tableRef} onScroll={onScroll}>
        <form className={styles.transaction} onSubmit={handleSubmit(onSubmit)}>
          {!profileLoaded && (
            <div
              className="flex items-center gap-2 self-center text-sm text-gray-500"
              style={{ marginTop: "25px", marginLeft: "5px" }}>
              <LoadingSpinnerSVG className="inline size-4 animate-spin text-text fill-primary" />
              Checking availability of reports for your account...
            </div>
          )}
          {profileLoaded && !noReportsAvailable ? (
            <Controller
              name="reportType"
              control={control}
              render={({ field: { value = availableReportTypes[0], onChange } }) => (
                <Select
                  label="&nbsp;&nbsp;Transactions"
                  options={availableReportTypes}
                  value={value}
                  onChange={onChange}
                  btnClass="!w-72"
                  disabled={isCarrier}
                />
              )}
            />
          ) : null}
          {profileLoaded && noReportsAvailable ? (
            <p
              className="w-72 self-center text-sm text-gray-500"
              style={{ marginTop: "25px", marginLeft: "5px" }}>
              No reports available for your account.
            </p>
          ) : null}

          <div className={styles.dateRange}>
            <Controller
              name="filterBy"
              control={control}
              render={({ field: { value = FILTER_BY_LIST[0], onChange } }) => (
                <Select
                  label="&nbsp;&nbsp;Filter by"
                  options={FILTER_BY_LIST}
                  value={value}
                  onChange={onChange}
                />
              )}
            />

            <span className="mb-3.5">OR</span>

            <Controller
              name="createdGte"
              control={control}
              render={({ field: { value = startDate, onChange } }) => (
                <DatePicker
                  label="&nbsp;&nbsp;Start Date"
                  placeholder="Select Date"
                  value={value}
                  onChange={(value) => {
                    onChange(value);
                    setStartDate(value);
                  }}
                  inputClass="!desktop:pb-3 !desktop:pt-3.5"
                />
              )}
            />

            <Controller
              name="createdLte"
              control={control}
              render={({ field: { value = endDate, onChange } }) => (
                <DatePicker
                  label="&nbsp;&nbsp;End Date"
                  placeholder="Select Date"
                  value={value}
                  onChange={(value) => {
                    onChange(value);
                    setEndDate(value);
                  }}
                  inputClass="!desktop:pb-3 !desktop:pt-3.5"
                />
              )}
            />
            {isDesktop ? (
              <Button
                type="submit"
                className="h-12 !border-y-2 !border-primary"
                loading={isSubmitting}
                disabled={!profileLoaded || noReportsAvailable || Boolean(pendingReportId)}>
                Request Report
              </Button>
            ) : null}
          </div>

          {!isDesktop ? (
            <Button
              type="submit"
              className="h-12 !border-y-2 !border-primary w-full mt-6"
              loading={isSubmitting}
              disabled={!profileLoaded || noReportsAvailable || Boolean(pendingReportId)}>
              Request Report
            </Button>
          ) : null}
        </form>

        <div className={styles.reportsWrapper}>
          {isDesktop ? (
            <DataTable
              data={enhancedReportList}
              headers={reportHeaderList}
              sortDisableList={["actions"]}
            />
          ) : (
            <>
              {enhancedReportList.length ? (
                enhancedReportList.map((item) => (
                  <ul key={item.id} className={styles.reportList}>
                    <li>
                      <span>Requested on</span>
                      <span>{dayjs(item.created).format("YYYY-MM-DD hh:mm A")}</span>
                    </li>
                    <li>
                      <span>Type</span>
                      <span className="capitalize">{item.reportType}</span>
                    </li>
                    <li>
                      <span>Details</span>
                      <span>{item.details}</span>
                    </li>
                    <li className="items-center">
                      <span>Actions</span>
                      <ReportAction
                        className={styles.button}
                        status={item.status}
                        error={item.error}
                        isLoading={loading}
                        onDownload={() => downloadReport(item.id)}
                      />
                    </li>
                  </ul>
                ))
              ) : (
                <h3 className={styles.noReports}>No reports</h3>
              )}
            </>
          )}
        </div>

        <StatusModal
          title="Success"
          isModalOpen={isConfirmModalOpen}
          toggleModal={toggleConfirmModal}
          backdropClick={toggleConfirmModal}
          content="We have received your request. Your report will be available soon."
        />
        <StatusModal
          title="Report Ready!"
          isModalOpen={isReportModalOpen}
          toggleModal={toggleReportModal}
          backdropClick={toggleReportModal}
          content="Your report has been generated and is ready to be viewed in the reports section."
        />
      </div>
    </MainLayout>
  );
}
